import { useEffect, useState } from 'react'
import axios from 'axios'
import {
  UserPlus,
  X,
  AlertCircle,
  Users,
  ChevronRight,
  ChevronDown,
  Laptop,
} from 'lucide-react'
import {
  getAccounts,
  createAccount,
  getAccountDevices,
  type CreateAccountRequest,
} from '../lib/api'
import { EmptyState } from './EmptyState'
import { StatusBadge } from './StatusBadge'
import { cn, timeAgo } from '../lib/utils'
import type { AccountDevice, MonitoredAccount } from '../types'

// Total column count of the accounts table — the device drawer's <td> spans
// all of them. Kept as a constant so the colSpan never drifts from the header.
const ACCOUNT_COLS = 7

// Admin surface for the repaired POST /accounts create path
// (DW-FE-ACCOUNTS-CREATE). Lists the accounts stored for the tenant (GET
// /accounts) and offers an honest "Add account" form wired to the live schema.
// A stored seat is not a monitored seat — this panel makes no monitoring claim.
//
// Two honesty rules govern this panel:
//  - The list renders ONLY an explicit allow-list of non-secret fields. The
//    response model carries no credentials (CyberCX F-07), but even if a
//    credential-shaped key ever appeared on a row we would not render it — see
//    the `SAFE` projection below. We never map over arbitrary account keys.
//  - The create call is the real backend. A 400/403/422 surfaces the server's
//    own detail verbatim; we never fake a success or invent a status the
//    response didn't return.

const TENANT_TYPES = [
  ['microsoft365', 'Microsoft 365'],
  ['google_workspace', 'Google Workspace'],
] as const

const MONITORING_LEVELS = [
  ['standard', 'Standard'],
  ['elevated', 'Elevated'],
] as const

// Extract an honest message from an axios error. FastAPI returns a string
// `detail` for HTTPException (400/403) and an array of {msg} for 422 request
// validation — surface both faithfully, never a generic "something happened".
function errorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail
    if (typeof detail === 'string' && detail) return detail
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) =>
          d && typeof d === 'object' && typeof d.msg === 'string' ? d.msg : null,
        )
        .filter((m): m is string => Boolean(m))
      if (msgs.length) return msgs.join('; ')
    }
  }
  return fallback
}

// Truncate a device_id for display without hiding that it's abbreviated. The
// full value is still exposed via `title` for copy/inspect, but the raw
// fingerprint blob is never available here — the type/api projection dropped it.
function truncateDeviceId(deviceId: string | null): string {
  if (!deviceId) return '—'
  return deviceId.length > 20 ? `${deviceId.slice(0, 20)}…` : deviceId
}

// The explicit, non-secret projection rendered in the list. Anything not named
// here — including any credential blob — is never read into the DOM. Each row
// carries a "Devices" expander that reveals the account's device registry.
function SafeRow({
  account,
  expanded,
  onToggle,
}: {
  account: MonitoredAccount
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-2 py-3">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide devices' : 'Show devices'}
            className="tap-min inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            )}
            Devices
          </button>
        </td>
        <td className="px-4 py-3 font-medium text-navy">{account.upn}</td>
        <td className="px-4 py-3 text-gray-600">{account.display_name ?? '—'}</td>
        <td className="px-4 py-3 text-gray-600">
          {account.tenant_type === 'microsoft365'
            ? 'Microsoft 365'
            : account.tenant_type === 'google_workspace'
              ? 'Google Workspace'
              : account.tenant_type}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={account.status} size="sm" />
        </td>
        <td className="px-4 py-3 text-gray-600 capitalize">
          {account.monitoring_level}
        </td>
        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
          {account.last_sync_at ? timeAgo(account.last_sync_at) : 'never'}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={ACCOUNT_COLS} className="bg-gray-50/60 px-4 py-3">
            <AccountDevicesDrawer accountId={account.id} />
          </td>
        </tr>
      )}
    </>
  )
}

// Per-account device registry. Loads GET /accounts/{id}/devices on mount (i.e.
// when the row is expanded) and renders ONLY the safe fields. Honesty rules:
//  - An empty registry says "No devices recorded … yet" — never "all clear".
//  - A load failure surfaces an honest error and shows nothing else, never a
//    reassuring empty table.
//  - Only the projected safe fields are rendered; the raw fingerprint blob was
//    already dropped at the api boundary, so it can never reach the DOM.
function AccountDevicesDrawer({ accountId }: { accountId: string }) {
  const [devices, setDevices] = useState<AccountDevice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    getAccountDevices(accountId)
      .then((data) => {
        if (alive) setDevices(data)
      })
      .catch(() => {
        if (alive) setError('Could not load devices for this account.')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [accountId])

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
        <div className="w-4 h-4 border-2 border-brass border-t-transparent rounded-full animate-spin" />
        Loading devices…
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-light p-3 text-sm text-danger"
        role="alert"
      >
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
        <span>{error} Nothing is shown because nothing could be loaded.</span>
      </div>
    )
  }

  if (devices.length === 0) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
        <Laptop className="w-4 h-4 shrink-0" aria-hidden="true" />
        No devices recorded for this account yet.
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Device</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Trust</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">First seen</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Last seen</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Revoked</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {devices.map((device) => (
            <tr key={device.id}>
              <td
                className="px-3 py-2 font-mono text-gray-700"
                title={device.device_id ?? undefined}
              >
                {truncateDeviceId(device.device_id)}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {device.trust_score.toFixed(2)}
              </td>
              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                {device.first_seen ? timeAgo(device.first_seen) : '—'}
              </td>
              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                {device.last_seen ? timeAgo(device.last_seen) : 'never'}
              </td>
              <td className="px-3 py-2">
                {device.is_revoked ? (
                  <span className="inline-flex items-center rounded-full bg-danger-light px-2 py-0.5 font-medium text-danger">
                    Revoked
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600">
                    Active
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface Props {
  // The admin's own tenant id. The backend IGNORES the body tenant and scopes
  // the new account to the authenticated admin, but the schema requires the
  // field — we pass this only to satisfy validation, never as a tenant picker.
  tenantId: string
}

export function AccountsPanel({ tenantId }: Props) {
  const [accounts, setAccounts] = useState<MonitoredAccount[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    getAccounts()
      .then((data) => setAccounts(data))
      .catch(() => setLoadError('Could not load accounts.'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-navy">Accounts</h2>
          <p className="text-xs text-gray-500">
            Accounts stored for this tenant. Credentials are never displayed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="tap-min inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark"
        >
          <UserPlus className="w-4 h-4" aria-hidden="true" />
          Add account
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-brass border-t-transparent rounded-full animate-spin" />
        </div>
      ) : loadError ? (
        <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span>{loadError} Nothing is shown because nothing could be loaded.</span>
        </div>
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No accounts on file"
          message="No accounts are stored for this tenant yet. When accounts are stored, they show here."
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-2 py-3 font-medium text-gray-600">
                    <span className="sr-only">Devices</span>
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">UPN</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Platform</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Account level</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Last sync</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {accounts.map((account) => (
                  <SafeRow
                    key={account.id}
                    account={account}
                    expanded={expandedId === account.id}
                    onToggle={() =>
                      setExpandedId((prev) =>
                        prev === account.id ? null : account.id,
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {formOpen && (
        <AddAccountForm
          tenantId={tenantId}
          onClose={() => setFormOpen(false)}
          onCreated={(account) => {
            // Prepend the real returned account (list is created_at desc).
            setAccounts((prev) => [account, ...prev])
            setFormOpen(false)
          }}
        />
      )}
    </div>
  )
}

interface FormProps {
  tenantId: string
  onClose: () => void
  onCreated: (account: MonitoredAccount) => void
}

function AddAccountForm({ tenantId, onClose, onCreated }: FormProps) {
  const [principalId, setPrincipalId] = useState('')
  const [tenantType, setTenantType] =
    useState<CreateAccountRequest['tenant_type']>('microsoft365')
  const [upn, setUpn] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [tenantProviderId, setTenantProviderId] = useState('')
  const [monitoringLevel, setMonitoringLevel] = useState('standard')
  const [credentialsJson, setCredentialsJson] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function closeIfIdle() {
    if (busy) return
    onClose()
  }

  async function submit() {
    setError(null)

    // Parse the optional credentials blob client-side so an obviously broken
    // JSON never reaches the wire — but this is a convenience check, not a
    // guarantee: the backend still validates and encrypts the real secret.
    let authCredentials: Record<string, unknown> | undefined
    const raw = credentialsJson.trim()
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          setError('Credentials must be a JSON object (e.g. {"client_id": "…"}).')
          return
        }
        authCredentials = parsed as Record<string, unknown>
      } catch {
        setError('Credentials are not valid JSON. Fix the JSON or leave it blank.')
        return
      }
    }

    const body: CreateAccountRequest = {
      // Required by the schema; the server ignores the value and scopes the
      // account to the authenticated admin's tenant. Not a tenant picker.
      tenant_id: tenantId,
      principal_id: principalId.trim(),
      tenant_type: tenantType,
      upn: upn.trim(),
      monitoring_level: monitoringLevel,
    }
    if (displayName.trim()) body.display_name = displayName.trim()
    if (tenantProviderId.trim()) body.tenant_provider_id = tenantProviderId.trim()
    if (authCredentials) body.auth_credentials = authCredentials

    setBusy(true)
    try {
      const account = await createAccount(body)
      onCreated(account)
    } catch (err) {
      // Honest server error — 400 (bad credentials), 403 (not admin), 422
      // (validation). The account is NOT added and nothing is faked.
      setError(
        errorMessage(err, 'Could not add the account. Nothing was created.'),
      )
    } finally {
      setBusy(false)
    }
  }

  const canSubmit = principalId.trim() !== '' && upn.trim() !== ''

  const field =
    'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add account"
      onClick={closeIfIdle}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-semibold text-navy">Add account</h3>
          <button
            type="button"
            onClick={closeIfIdle}
            disabled={busy}
            aria-label="Close"
            className="tap-min text-gray-400 hover:text-navy disabled:opacity-50"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Principal id</span>
            <input
              type="text"
              value={principalId}
              onChange={(e) => setPrincipalId(e.target.value)}
              placeholder="UUID of the person this account belongs to"
              aria-label="Principal id"
              className={cn(field, 'font-mono text-xs')}
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Platform</span>
            <select
              value={tenantType}
              onChange={(e) =>
                setTenantType(e.target.value as CreateAccountRequest['tenant_type'])
              }
              aria-label="Platform"
              className={field}
            >
              {TENANT_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">UPN (email)</span>
            <input
              type="email"
              value={upn}
              onChange={(e) => setUpn(e.target.value)}
              placeholder="user@tenant.example"
              aria-label="UPN"
              className={field}
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600">
                Display name (optional)
              </span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                aria-label="Display name"
                className={field}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">Account level</span>
              <select
                value={monitoringLevel}
                onChange={(e) => setMonitoringLevel(e.target.value)}
                aria-label="Account level"
                className={field}
              >
                {MONITORING_LEVELS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">
              Tenant provider id (optional)
            </span>
            <input
              type="text"
              value={tenantProviderId}
              onChange={(e) => setTenantProviderId(e.target.value)}
              placeholder="Azure/Google tenant id, if known"
              aria-label="Tenant provider id"
              className={cn(field, 'font-mono text-xs')}
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">
              Auth credentials (optional, JSON)
            </span>
            <textarea
              value={credentialsJson}
              onChange={(e) => setCredentialsJson(e.target.value)}
              rows={4}
              aria-label="Auth credentials JSON"
              placeholder={'{"client_id": "…", "client_secret": "…"}  or  {"refresh_token": "…"}'}
              className={cn(field, 'font-mono text-xs')}
            />
            <span className="mt-1 block text-[11px] text-gray-400">
              Encrypted at rest by the server and never shown again. Leave blank to
              add the account without credentials and configure consent later.
            </span>
          </label>

          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={closeIfIdle}
              disabled={busy}
              className="tap-min px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !canSubmit}
              className="tap-min px-3 py-2 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark disabled:opacity-50"
            >
              {busy ? 'Adding…' : 'Add account'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
